/**
 * Ikon garis pada kanvas 24, ujung dan sambungan bulat.
 *
 * Nama ikon datang dari manifest (`nav[].icon`), bukan dipilih layar. Ikon yang
 * tidak dikenal menjadi titik netral alih-alih kotak kosong — sebuah modul baru
 * tidak boleh merusak sidebar hanya karena ikonnya belum digambar.
 *
 * Goresan dijaga konstan secara optis: `visualStroke × 24 / ukuranRender`
 * (token `icon`).
 */
import type { ReactElement } from 'react';

export function Icon({ name, size = 20 }: { name: string; size?: number }) {
  const strokeWidth = (1.7 * 24) / size;
  const p = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  const paths: Record<string, ReactElement> = {
    'layout-dashboard': (
      <>
        <rect x="3" y="3" width="7" height="9" rx="1.5" {...p} />
        <rect x="14" y="3" width="7" height="5" rx="1.5" {...p} />
        <rect x="14" y="12" width="7" height="9" rx="1.5" {...p} />
        <rect x="3" y="16" width="7" height="5" rx="1.5" {...p} />
      </>
    ),
    'clipboard-list': (
      <>
        <path d="M9 4h6v2.5H9z" {...p} />
        <path d="M9 5H6.5A1.5 1.5 0 005 6.5v13A1.5 1.5 0 006.5 21h11a1.5 1.5 0 001.5-1.5v-13A1.5 1.5 0 0017.5 5H15" {...p} />
        <path d="M8.5 11h7M8.5 15h5" {...p} />
      </>
    ),
    'clipboard-check': (
      <>
        <path d="M9 4h6v2.5H9z" {...p} />
        <path d="M9 5H6.5A1.5 1.5 0 005 6.5v13A1.5 1.5 0 006.5 21h11a1.5 1.5 0 001.5-1.5v-13A1.5 1.5 0 0017.5 5H15" {...p} />
        <path d="M8.5 13.5l2.4 2.4 4.6-4.9" {...p} />
      </>
    ),
    'users-round': (
      <>
        <circle cx="10" cy="8" r="3.4" {...p} />
        <path d="M3.6 20a6.6 6.6 0 0112.8 0" {...p} />
        <path d="M16.2 5.2a3.4 3.4 0 010 5.7M17.8 14.4A6.6 6.6 0 0120.4 20" {...p} />
      </>
    ),
    'bar-chart-3': (
      <>
        <path d="M4 20V4" {...p} />
        <path d="M4 20h16" {...p} />
        <path d="M8.5 20v-6M13 20V8.5M17.5 20v-3.5" {...p} />
      </>
    ),
    'map-pin': (
      <>
        <path d="M12 21s7-5.6 7-11a7 7 0 10-14 0c0 5.4 7 11 7 11z" {...p} />
        <circle cx="12" cy="10" r="2.6" {...p} />
      </>
    ),
    users: (
      <>
        <circle cx="9" cy="8" r="3.4" {...p} />
        <path d="M2.8 20a6.2 6.2 0 0112.4 0" {...p} />
        <circle cx="17.5" cy="9" r="2.6" {...p} />
        <path d="M16 14.4A5.6 5.6 0 0121.2 20" {...p} />
      </>
    ),
    award: (
      <>
        <circle cx="12" cy="9" r="5.4" {...p} />
        <path d="M8.6 13.6L7.4 21l4.6-2.4 4.6 2.4-1.2-7.4" {...p} />
      </>
    ),
    'building-2': (
      <>
        <path d="M4 21V6.2A1.2 1.2 0 015.2 5h5.6A1.2 1.2 0 0112 6.2V21" {...p} />
        <path d="M12 21V10.6a1.2 1.2 0 011.2-1.2h5.6A1.2 1.2 0 0120 10.6V21" {...p} />
        <path d="M3 21h18M7 9h2M7 13h2M15.5 13h1.5M15.5 17h1.5" {...p} />
      </>
    ),
    layers: (
      <>
        <path d="M12 3l9 5-9 5-9-5 9-5z" {...p} />
        <path d="M3 13l9 5 9-5" {...p} />
      </>
    ),
    'scroll-text': (
      <>
        <path d="M5 5.5A2.5 2.5 0 017.5 3h9A2.5 2.5 0 0119 5.5V19a2 2 0 01-2 2H7a2 2 0 01-2-2z" {...p} />
        <path d="M9 8h6M9 12h6M9 16h3.5" {...p} />
      </>
    ),
    activity: <path d="M3 12h3.5l2.5-7 4 14 2.5-7H21" {...p} />,
    search: (
      <>
        <circle cx="11" cy="11" r="6.4" {...p} />
        <path d="M15.8 15.8L21 21" {...p} />
      </>
    ),
    'arrow-left': (
      <>
        <path d="M20 12H4" {...p} />
        <path d="M10 6l-6 6 6 6" {...p} />
      </>
    ),
    /* Ikon globe pengalih bahasa. Figma `70:1445` menamainya `ikon/bahasa` dan
       memakainya 16 px di depan label bahasa (audit K-10). */
    globe: (
      <>
        <circle cx="12" cy="12" r="8.4" {...p} />
        <path d="M3.6 12h16.8" {...p} />
        <path d="M12 3.6c2.1 2.3 3.2 5.2 3.2 8.4S14.1 18.1 12 20.4c-2.1-2.3-3.2-5.2-3.2-8.4S9.9 5.9 12 3.6z" {...p} />
      </>
    ),
    camera: (
      <>
        <path d="M3 8.6A1.6 1.6 0 014.6 7h2.6l1.4-2h6.8l1.4 2h2.6A1.6 1.6 0 0121 8.6v9.8A1.6 1.6 0 0119.4 20H4.6A1.6 1.6 0 013 18.4z" {...p} />
        <circle cx="12" cy="13" r="3.6" {...p} />
      </>
    ),
  };

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {paths[name] ?? <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />}
    </svg>
  );
}
